import React from "react";

import { useFormikContext } from "formik";
import { GroupBase } from "react-select";

import { EngineEnum } from "../../../../enums/engine";
import { Template } from "../../../../models";
import { getEngineName, getEngineSVG } from "../../../../utils";

import SelectV2, { OptionType } from "../../../../components/form/CustomSelect";
import TemplateContainer from "../common/TemplateContainer";

// matches is_active=false rows in the `engine` table - checked 2026-07-27
// GPT_5_2025_08_07 and CLAUDE_SONNET_4_5 deprecated 2026-07-28 when they were replaced as
// primary/secondary by GPT_5_6_SOL / CLAUDE_SONNET_5 in gen/gen_article.py
const DEPRECATED_ENGINES = new Set([
  EngineEnum.CLAUDE_3_OPUS_20240229,
  EngineEnum.CLAUDE_3_5_SONNET_20240620,
  EngineEnum.CLAUDE_3_5_SONNET_20241022,
  EngineEnum.CLAUDE_3_5_HAIKU_20241022,
  EngineEnum.CLAUDE_3_7_SONNET_20250219,
  EngineEnum.CLAUDE_SONNET_4_5,
  EngineEnum.GPT_4_TURBO_2024_04_09,
  EngineEnum.O3_MINI_2025_01_31,
  EngineEnum.GEMINI_1_5_PRO,
  EngineEnum.GPT_5_2025_08_07,
  EngineEnum.GPT_5_MINI_2025_08_07,
  EngineEnum.GPT_5_NANO_2025_08_07,
]);

// what to use instead of each deprecated engine, shown in the info icon next to the "Deprecated" badge
const REPLACEMENT_ENGINE: Partial<Record<EngineEnum, EngineEnum>> = {
  [EngineEnum.CLAUDE_3_OPUS_20240229]: EngineEnum.CLAUDE_OPUS_5,
  [EngineEnum.CLAUDE_3_5_SONNET_20240620]: EngineEnum.CLAUDE_SONNET_5,
  [EngineEnum.CLAUDE_3_5_SONNET_20241022]: EngineEnum.CLAUDE_SONNET_5,
  [EngineEnum.CLAUDE_3_5_HAIKU_20241022]: EngineEnum.CLAUDE_HAIKU_4_5_20251001,
  [EngineEnum.CLAUDE_3_7_SONNET_20250219]: EngineEnum.CLAUDE_SONNET_5,
  [EngineEnum.CLAUDE_SONNET_4_5]: EngineEnum.CLAUDE_SONNET_5,
  [EngineEnum.GPT_4_TURBO_2024_04_09]: EngineEnum.GPT_5_6_SOL,
  [EngineEnum.O3_MINI_2025_01_31]: EngineEnum.GPT_5_6_TERRA,
  [EngineEnum.GEMINI_1_5_PRO]: EngineEnum.GEMINI_3_6_FLASH,
  [EngineEnum.GPT_5_2025_08_07]: EngineEnum.GPT_5_6_SOL,
  [EngineEnum.GPT_5_MINI_2025_08_07]: EngineEnum.GPT_5_6_TERRA,
  [EngineEnum.GPT_5_NANO_2025_08_07]: EngineEnum.GPT_5_6_LUNA,
};

// replacements added for the engines above - added 2026-07-27
const NEW_ENGINES = new Set([
  EngineEnum.CLAUDE_SONNET_5,
  EngineEnum.CLAUDE_OPUS_5,
  EngineEnum.CLAUDE_HAIKU_4_5_20251001,
  EngineEnum.GPT_5_6_SOL,
  EngineEnum.GEMINI_3_6_FLASH,
  EngineEnum.GPT_5_6_TERRA,
  EngineEnum.GPT_5_6_LUNA,
  EngineEnum.GEMINI_3_5_FLASH_LITE,
  EngineEnum.GPT_5_4_MINI,
  EngineEnum.GPT_5_4_NANO,
]);

// must match primary_engine_id / secondary_engine_id in gen/gen_article.py
const PRIMARY_ENGINE = EngineEnum.GPT_5_6_SOL;
const SECONDARY_ENGINE = EngineEnum.CLAUDE_SONNET_5;

// shutdown date (mm/dd/yyyy, per provider's own docs - "not announced" where none is published yet)
// and cost per 1M tokens - checked 2026-07-27. Only covers active (non-deprecated) engines.
const ENGINE_INFO: Partial<
  Record<
    EngineEnum,
    { shutdownDate?: string; inputCost: number; outputCost: number }
  >
> = {
  [EngineEnum.CLAUDE_SONNET_4_5]: {
    // shutdown date omitted - deprecated in this codebase, "Use claude-sonnet-5" is the only
    // message needed here
    inputCost: 3,
    outputCost: 15,
  },
  [EngineEnum.CLAUDE_SONNET_5]: {
    inputCost: 3,
    outputCost: 15,
  },
  [EngineEnum.CLAUDE_OPUS_5]: {
    inputCost: 5,
    outputCost: 25,
  },
  [EngineEnum.CLAUDE_HAIKU_4_5_20251001]: {
    inputCost: 1,
    outputCost: 5,
  },
  [EngineEnum.GPT_5_2025_08_07]: {
    // shutdown date omitted - deprecated in this codebase, "Use gpt-5.6-sol" is the only
    // message needed here
    inputCost: 1.25,
    outputCost: 10,
  },
  [EngineEnum.GPT_5_6_SOL]: { inputCost: 5, outputCost: 30 },
  [EngineEnum.GPT_5_6_TERRA]: { inputCost: 2.5, outputCost: 15 },
  [EngineEnum.GPT_5_6_LUNA]: { inputCost: 1, outputCost: 6 },
  [EngineEnum.GPT_5_4_MINI]: { inputCost: 0.75, outputCost: 4.5 },
  [EngineEnum.GPT_5_4_NANO]: { inputCost: 0.2, outputCost: 1.25 },
  // shutdown date per Azure Foundry's retirement schedule (not listed on OpenAI's own direct-API deprecations page)
  [EngineEnum.GPT_4O_MINI_2024_07_18]: { inputCost: 0.15, outputCost: 0.6 },
  [EngineEnum.GEMINI_3_6_FLASH]: { inputCost: 1.5, outputCost: 7.5 },
  [EngineEnum.GEMINI_3_5_FLASH_LITE]: { inputCost: 0.3, outputCost: 2.5 },
};

const formatCost = (value: number) =>
  Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;

const buildEngineOption = (engine: EngineEnum) => {
  const isDeprecated = DEPRECATED_ENGINES.has(engine);
  const badge = isDeprecated
    ? ("deprecated" as const)
    : engine === PRIMARY_ENGINE
      ? ("primary" as const)
      : engine === SECONDARY_ENGINE
        ? ("secondary" as const)
        : NEW_ENGINES.has(engine)
          ? ("new" as const)
          : undefined;

  const replacement = REPLACEMENT_ENGINE[engine];
  const info = ENGINE_INFO[engine];

  return {
    value: engine,
    label: getEngineName(engine),
    icon: getEngineSVG(engine),
    description: "",
    isDisabled: isDeprecated,
    badge,
    badgeTooltip:
      isDeprecated && replacement
        ? `Use ${getEngineName(replacement)}`
        : undefined,
    infoTooltip: info?.shutdownDate
      ? `Shutdown: ${info.shutdownDate}`
      : undefined,
    costTooltip: info
      ? `${formatCost(info.inputCost)} in / ${formatCost(info.outputCost)} out per 1M tokens`
      : undefined,
  };
};

const ANTHROPIC_ENGINES = [
  EngineEnum.CLAUDE_SONNET_5,
  EngineEnum.CLAUDE_OPUS_5,
  EngineEnum.CLAUDE_HAIKU_4_5_20251001,
];
const OPENAI_ENGINES = [
  EngineEnum.GPT_5_6_SOL,
  EngineEnum.GPT_5_6_TERRA,
  EngineEnum.GPT_5_6_LUNA,
  EngineEnum.GPT_5_4_MINI,
  EngineEnum.GPT_5_4_NANO,
  EngineEnum.GPT_4O_MINI_2024_07_18,
];
const GEMINI_ENGINES = [
  EngineEnum.GEMINI_3_6_FLASH,
  EngineEnum.GEMINI_3_5_FLASH_LITE,
];
const DEPRECATED_ENGINES_ORDERED = [
  EngineEnum.CLAUDE_3_7_SONNET_20250219,
  EngineEnum.CLAUDE_3_OPUS_20240229,
  EngineEnum.CLAUDE_3_5_SONNET_20240620,
  EngineEnum.CLAUDE_3_5_SONNET_20241022,
  EngineEnum.CLAUDE_3_5_HAIKU_20241022,
  EngineEnum.CLAUDE_SONNET_4_5,
  EngineEnum.GPT_5_2025_08_07,
  EngineEnum.GPT_4_TURBO_2024_04_09,
  EngineEnum.O3_MINI_2025_01_31,
  EngineEnum.GPT_5_MINI_2025_08_07,
  EngineEnum.GPT_5_NANO_2025_08_07,
  EngineEnum.GEMINI_1_5_PRO,
];

// flat list - used for looking up the currently selected engine's option
export const engineOptions = [
  ...OPENAI_ENGINES,
  ...ANTHROPIC_ENGINES,
  ...GEMINI_ENGINES,
  ...DEPRECATED_ENGINES_ORDERED,
].map(buildEngineOption);

// grouped by vendor (with deprecated grouped separately at the end) - used to render the dropdown
export const engineOptionGroups: GroupBase<OptionType>[] = [
  { label: "OpenAI", options: OPENAI_ENGINES.map(buildEngineOption) },
  { label: "Anthropic", options: ANTHROPIC_ENGINES.map(buildEngineOption) },
  { label: "Gemini", options: GEMINI_ENGINES.map(buildEngineOption) },
  {
    label: "Deprecated",
    options: DEPRECATED_ENGINES_ORDERED.map(buildEngineOption),
  },
];

const Engine = () => {
  const { values, setFieldValue } = useFormikContext<Partial<Template>>();

  const selectedEngine = engineOptions.find(
    (option) => option.value === values?.engineId
  );

  return (
    <div className="animate-fadeIn">
      <TemplateContainer isSuperUserFeature>
        <SelectV2
          label="Template Engine"
          placeholder="-"
          options={engineOptionGroups}
          selected={selectedEngine}
          setSelected={(option) => {
            setFieldValue("engineId", option?.value ?? null);
          }}
          maxMenuHeight={450}
          menuPlacement="top"
        />
      </TemplateContainer>
    </div>
  );
};

export default Engine;
